import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Loader2, ArrowLeft } from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import { getLiveIndustries } from "@/lib/industries";

const LIVE_INDUSTRIES = getLiveIndustries();

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLogin, setIsLogin] = useState(!searchParams.has("signup"));
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState(searchParams.get("industry") || "");

  // Preserve a same-origin `next` path so OAuth consent (and other deep links)
  // return to the original URL after sign-in / sign-up.
  const rawNext = searchParams.get("next") || "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";
  const postAuthRedirect = `${typeof window !== "undefined" ? window.location.origin : "https://www.sentinelforgesystems.com"}${nextPath}`;

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: postAuthRedirect,
      });
      if (result.error) {
        toast({ title: "Error", description: result.error.message, variant: "destructive" });
        return;
      }
      if (result.redirected) return;
      navigate(nextPath);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast({
        title: "Check your email",
        description: "If an account exists with that email, we've sent a password reset link. Please check your inbox and spam folder.",
      });
      setIsForgotPassword(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate(nextPath);
      } else {
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              ...(selectedIndustry ? { industry: selectedIndustry } : {}),
            },
            emailRedirectTo: postAuthRedirect,
          },
        });
        if (error) throw error;
        supabase.functions.invoke("send-email", {
          body: { type: "welcome", to: email, userName: fullName, industry: selectedIndustry || "security" },
        }).catch(() => {});
        toast({
          title: "Check your email",
          description: "We sent you a confirmation link to verify your account.",
        });
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const backLink = (
    <Link
      to="/"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to home
    </Link>
  );

  if (isForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {backLink}
          <Card className="border-border bg-card shadow-elevated">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-12 w-12 rounded-xl object-contain" />
              </div>
              <CardTitle className="text-2xl text-foreground">Reset Password</CardTitle>
              <CardDescription className="text-muted-foreground">
                Enter your email and we'll send you a reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    required
                    className="mt-1 bg-secondary border-border text-foreground"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Reset Link
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="text-sm text-primary hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {backLink}
        <Card className="border-border bg-card shadow-elevated">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-12 w-12 rounded-xl object-contain" />
            </div>
            <CardTitle className="text-2xl text-foreground">Sentinel Forge Systems</CardTitle>
            <CardDescription className="text-muted-foreground">
              {isLogin ? "Sign in to your account" : "Create a new account"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <Label className="text-muted-foreground">Full Name</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Doe"
                      required={!isLogin}
                      className="mt-1 bg-secondary border-border text-foreground"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Industry</Label>
                    {LIVE_INDUSTRIES.length === 1 ? (
                      <div className="grid grid-cols-1 gap-2 mt-1">
                        {LIVE_INDUSTRIES.map((ind) => (
                          <button
                            key={ind.key}
                            type="button"
                            onClick={() => setSelectedIndustry(ind.key)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                              selectedIndustry === ind.key
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border bg-secondary text-muted-foreground hover:border-primary/30"
                            }`}
                          >
                            <ind.icon className="h-4 w-4 shrink-0" />
                            {ind.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                        <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                          <SelectValue placeholder="Select your industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {LIVE_INDUSTRIES.map((ind) => (
                            <SelectItem key={ind.key} value={ind.key}>
                              {ind.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-muted-foreground mt-1.5">
                      More industries coming soon.
                    </p>
                  </div>
                </>
              )}
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  className="mt-1 bg-secondary border-border text-foreground"
                />
              </div>
              <div>
                <Label className="text-muted-foreground">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="mt-1 bg-secondary border-border text-foreground"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLogin ? "Sign In" : "Create Account"}
              </Button>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className="w-full border-border text-foreground hover:bg-secondary"
            >
              {isGoogleLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              Continue with Google
            </Button>

            <div className="mt-4 text-center space-y-2">
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="block w-full text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  Forgot your password?
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-primary hover:underline"
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
